import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, PartyPopper, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/marketing/BrandMark";
import { api, inputCls, labelCls, btnPrimary, CATEGORIES, splitWhatsapp, MAX_SERVICES, POLICY_SUGGESTIONS } from "@/lib/api";

const slugify = (v) => v.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s-]+/g, "-").replace(/^-|-$/g, "");

const StepShell = ({ title, sub, children }) => (
  <div>
    <h1 className="font-serif text-3xl font-medium leading-tight tracking-tight text-guinep-ink">{title}</h1>
    <p className="mt-2 text-sm text-guinep-mute">{sub}</p>
    <div className="mt-8 space-y-5">{children}</div>
  </div>
);

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "", category: "", specialty: "", firstName: "", location: "", whatsapp: "",
    instagram: "", slug: "", slugTouched: false,
    services: [{ title: "", price: "", duration: "", consultation_required: false }],
    policies: {},
    policyLabels: {},
  });

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === "name" && !f.slugTouched ? { slug: slugify(v) } : {}) }));
  };

  const setService = (i, k, v) =>
    setForm((f) => ({ ...f, services: f.services.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));

  const finish = async () => {
    setBusy(true);
    setError("");
    try {
      const { cc, local } = splitWhatsapp(form.whatsapp);
      await api("/business/me", {
        method: "PUT",
        body: JSON.stringify({
          name: form.name, owner_first_name: form.firstName, category: form.category,
          specialty: form.specialty,
          location: form.location, instagram: form.instagram, slug: form.slug || form.name,
          whatsapp_country_code: cc, whatsapp_number: local, is_live: true, taking_appointments: true,
          policies: form.policies, policy_labels: form.policyLabels,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        }),
      });
      for (const [i, s] of form.services.entries()) {
        if (s.title.trim()) {
          await api("/services", { method: "POST", body: JSON.stringify({ ...s, order: i }) });
        }
      }
      toast.success("Your Guinep Seed page is ready!");
      setDone(true);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (step === 0 && (!form.name.trim() || !form.category)) {
      setError("Add your business name and type to continue.");
      return;
    }
    setError("");
    if (step < 3) setStep(step + 1);
    else finish();
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F7F5] px-5 font-sans" data-testid="onboarding-success">
        <div className="w-full max-w-sm text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-guinep-soft text-guinep-deep">
            <PartyPopper size={22} />
          </span>
          <h1 className="mt-6 font-serif text-3xl font-medium tracking-tight text-guinep-ink">Your Guinep Seed page is ready</h1>
          <p className="mt-2 text-sm text-guinep-mute">Share it anywhere you connect with clients.</p>
          <div className="mt-8 space-y-3">
            <Link to={`/${form.slug}`} data-testid="view-my-page-button" className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-guinep-line bg-white py-3.5 text-sm font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft">
              <ExternalLink size={15} /> View my page
            </Link>
            <button data-testid="go-to-dashboard-button" onClick={() => navigate("/app")} className={btnPrimary}>
              Go to my dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F5] px-5 py-10 font-sans" data-testid="onboarding-page">
      <div className="mx-auto max-w-sm">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-guinep-soft">
            <BrandMark size={16} />
          </span>
          <span className="font-serif text-lg font-semibold tracking-tight text-guinep-ink">Guinep Seed</span>
        </div>
        <div className="mt-5 flex gap-1.5" data-testid="onboarding-progress">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-guinep-deep" : "bg-guinep-line"}`} />
          ))}
        </div>

        <div className="mt-9">
          {step === 0 && (
            <StepShell title="What is your business called?" sub="And the kind of appointment work you do.">
              <div>
                <label className={labelCls}>Business name</label>
                <input data-testid="ob-business-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Crowned Roots" />
              </div>
              <div>
                <label className={labelCls}>Business type</label>
                <select data-testid="ob-business-type" className={inputCls} value={form.category} onChange={set("category")}>
                  <option value="">Select…</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Business specialty <span className="font-normal text-guinep-mute/60">(optional)</span></label>
                <input data-testid="ob-specialty" className={inputCls} value={form.specialty} onChange={set("specialty")} placeholder="Loctician, Nail Technician, Caterer, Photographer, Tutor" />
                <p className="mt-1.5 text-xs text-guinep-mute/70">Tell us what you do.</p>
              </div>
              <div>
                <label className={labelCls}>Your first name</label>
                <input data-testid="ob-first-name" className={inputCls} value={form.firstName} onChange={set("firstName")} placeholder="Maya" />
              </div>
            </StepShell>
          )}

          {step === 1 && (
            <StepShell title="Where are you located?" sub="And how clients usually reach you.">
              <div>
                <label className={labelCls}>Location or service area</label>
                <input data-testid="ob-location" className={inputCls} value={form.location} onChange={set("location")} placeholder="St. John's, Antigua" />
              </div>
              <div>
                <label className={labelCls}>WhatsApp number</label>
                <input data-testid="ob-whatsapp" className={inputCls} value={form.whatsapp} onChange={set("whatsapp")} placeholder="12685550142" />
                <p className="mt-1.5 text-xs text-guinep-mute/70">Use the number where you want client enquiries to arrive. Include your country code.</p>
              </div>
              <div>
                <label className={labelCls}>Instagram (optional)</label>
                <input data-testid="ob-instagram" className={inputCls} value={form.instagram} onChange={set("instagram")} placeholder="crownedroots" />
              </div>
              <div>
                <label className={labelCls}>Your page link</label>
                <div className="flex items-center overflow-hidden rounded-xl border border-guinep-line bg-white">
                  <span className="border-r border-guinep-line bg-guinep-soft/60 px-3 py-3 text-xs text-guinep-mute">{window.location.host}/</span>
                  <input
                    data-testid="ob-slug"
                    className="flex-1 px-3 py-3 text-sm text-guinep-ink outline-none"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: slugify(e.target.value), slugTouched: true }))}
                  />
                </div>
              </div>
            </StepShell>
          )}

          {step === 2 && (
            <StepShell title="What services do you offer?" sub="Add 1–5 to start. You can change these anytime.">
              {form.services.map((s, i) => (
                <div key={i} className="rounded-2xl border border-guinep-line/70 bg-white p-4">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-guinep-mute/70">Service {i + 1}</p>
                  <input data-testid={`ob-service-title-${i}`} className={inputCls} value={s.title} onChange={(e) => setService(i, "title", e.target.value)} placeholder="Retwist" />
                  <div className="mt-2.5 flex gap-2.5">
                    <input data-testid={`ob-service-price-${i}`} className={inputCls} value={s.price} onChange={(e) => setService(i, "price", e.target.value)} placeholder="Price e.g. 85" />
                    <input data-testid={`ob-service-duration-${i}`} className={inputCls} value={s.duration} onChange={(e) => setService(i, "duration", e.target.value)} placeholder="Approx. 2 hours" />
                  </div>
                  <label className="mt-3 flex items-center gap-2 text-sm text-guinep-mute">
                    <input type="checkbox" data-testid={`ob-service-consult-${i}`} checked={s.consultation_required} onChange={(e) => setService(i, "consultation_required", e.target.checked)} className="h-4 w-4 accent-[#1F3A2B]" />
                    Consultation required
                  </label>
                </div>
              ))}
              {form.services.length < 5 && (
                <button
                  data-testid="ob-add-service"
                  onClick={() => setForm((f) => ({ ...f, services: [...f.services, { title: "", price: "", duration: "", consultation_required: false }] }))}
                  className="w-full rounded-xl border border-dashed border-guinep-line py-3.5 text-sm font-medium text-guinep-mute transition-colors hover:border-guinep-mid hover:text-guinep-ink"
                >
                  + Add another service
                </button>
              )}
            </StepShell>
          )}

          {step === 3 && (
            <StepShell title="What should clients know before booking?" sub="Keep it short and clear. Optional — skip any you like.">
              {(form.category === "Other Service Business"
                ? [{ key: "custom_1" }, { key: "custom_2" }, { key: "custom_3" }]
                : [...(POLICY_SUGGESTIONS[form.category] || []).slice(0, 3).map(([key, label]) => ({ key, label })), { key: "other", label: "Anything else" }]
              ).map((p) => (
                <div key={p.key}>
                  {p.label ? (
                    <label className={labelCls}>{p.label}</label>
                  ) : (
                    <input
                      className={`${inputCls} mb-2`}
                      value={form.policyLabels[p.key] || ""}
                      onChange={(e) => setForm((f) => ({ ...f, policyLabels: { ...f.policyLabels, [p.key]: e.target.value } }))}
                      placeholder="Policy name (e.g. Booking information)"
                    />
                  )}
                  <textarea
                    data-testid={`ob-policy-${p.key}`}
                    className={`${inputCls} min-h-[70px] resize-none`}
                    value={form.policies[p.key] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, policies: { ...f.policies, [p.key]: e.target.value } }))}
                    placeholder="Add your wording…"
                  />
                </div>
              ))}
            </StepShell>
          )}

          {error && <p data-testid="ob-error" className="mt-5 text-sm text-red-600">{error}</p>}

          <div className="mt-8 flex gap-3">
            {step > 0 && (
              <button data-testid="ob-back" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 rounded-xl border border-guinep-line bg-white px-5 py-3.5 text-sm font-semibold text-guinep-ink transition-colors hover:bg-guinep-soft">
                <ArrowLeft size={15} /> Back
              </button>
            )}
            <button data-testid="ob-continue" onClick={next} disabled={busy} className={btnPrimary}>
              {step === 3 ? (busy ? "Creating…" : "Create my page") : "Continue"} <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
